import { useState, useEffect, useCallback, Fragment } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import toastService from '@/services/toast/service';
import { 
  Users, 
  ArrowLeft, 
  ExternalLink,
  Trash2,
  Info,
  Building2,
  Mail,
  FileText,
  CalendarCheck,
  XCircle,
  Clock
} from 'lucide-react';
import { 
  agenciesService, 
  AgencyChurn,
  AgencyDetailRecord, 
  AgencySubaccount, 
  AgencyTeamMember,
  PaginationMeta,
} from '@/services/agencies';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BILLING_STATUS_CONFIG, CHURN_TYPE_CONFIG, memberStatusTone } from '@/lib/billingStatus';
import { useAuth } from '@/contexts/AuthContext';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AgencyDetailSkeleton, TableSkeleton } from '@/components/skeletons';
import { EditAgencyModal } from './EditAgencyModal';
import { EditSubmissionUsageModal } from './EditSubmissionUsageModal';
import { ActivityLogTimeline } from './components/ActivityLogTimeline';
import { AttributeAffiliateModal } from './components/AttributeAffiliateModal';
import { SetupWizardCard } from './components/SetupWizardCard';
import AgencyXrayPanel from './AgencyXrayPanel';
import { RelativeTime } from '@/components/RelativeTime';
import { formatPhoneDisplay, phoneCountry, countryName } from '@/lib/utils';
import { listPathFor } from '@/lib/campaigns-url';
import { TruncatedValue } from '@/components/TruncatedValue';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsViewer, useCanShadowAccess, useCanEditSubmissionUsage, VIEWER_NO_PERMISSION, NO_CLIENT_ACCESS } from '@/hooks/useIsViewer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import type { DateRange } from 'react-day-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

function getStatusBadge(billingStatus: string) {
  // The API's computed status, the one the agencies list shows. The raw Stripe
  // status reads as Free once a cancelled agency has been downgraded.
  const config = BILLING_STATUS_CONFIG[billingStatus] || BILLING_STATUS_CONFIG.free;
  return (
    <Tooltip>
      <TooltipTrigger className="cursor-default">
        <Badge tone={config.tone}>
          {config.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p>{config.tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

// The panel's title already says "Cancelling", so these drop the prefix.
const CHURN_TAG_LABELS: Record<string, string> = {
  cancelling_trial: 'Was trialing',
  cancelling_paid: 'Was paying',
};

function pluralDays(n: number, word = 'day') {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** Why and when a cancelled or cancelling agency left, at the top of the billing card. */
function ChurnPanel({ churn, billingStatus, timezone }: { churn: AgencyChurn; billingStatus: string; timezone?: string }) {
  const cancelling = billingStatus === 'cancelling';
  const trial = churn.type === 'trial_churn' || churn.type === 'cancelling_trial';
  const title = (BILLING_STATUS_CONFIG[billingStatus] ?? BILLING_STATUS_CONFIG.cancelled).label;
  const tag = CHURN_TAG_LABELS[churn.type] ?? CHURN_TYPE_CONFIG[churn.type]?.label;
  const tone = cancelling
    ? { band: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50', text: 'text-amber-700 dark:text-amber-400', icon: 'text-amber-600 dark:text-amber-400', edge: 'border-amber-200 dark:border-amber-900/50' }
    : { band: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900/50', text: 'text-red-700 dark:text-red-400', icon: 'text-red-600 dark:text-red-400', edge: 'border-red-200 dark:border-red-900/50' };
  const Icon = cancelling ? Clock : XCircle;

  // An empty saved timezone means the browser's own, as in RelativeTime.
  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: timezone || undefined, month: 'short', day: 'numeric', year: 'numeric' }) : null;

  const endsAt = churn.endsAtUtc ? new Date(churn.endsAtUtc).getTime() : null;
  let summary: string | null = null;
  if (cancelling) {
    if (endsAt !== null) {
      const daysLeft = Math.ceil((endsAt - Date.now()) / DAY_MS);
      summary = daysLeft > 0 ? `Still has access for ${pluralDays(daysLeft, 'more day')}` : 'Access ends today';
    }
  } else if (!churn.cancelledAtUtc) {
    summary = 'No cancel request on record';
  } else if (endsAt !== null) {
    const daysBefore = Math.floor((endsAt - new Date(churn.cancelledAtUtc).getTime()) / DAY_MS);
    const period = trial ? 'the trial' : 'the period';
    summary = daysBefore > 0 ? `Cancelled ${pluralDays(daysBefore)} before ${period} ended` : `Cancelled the day ${period} ended`;
  }

  const facts = [
    { label: 'Cancelled', value: formatDate(churn.cancelledAtUtc) },
    { label: cancelling ? 'Access ends' : trial ? 'Trial ended' : 'Access ended', value: formatDate(churn.endsAtUtc) },
    { label: cancelling ? 'Plan' : 'Was on', value: churn.planName ? `${churn.planName}${trial ? ' trial' : ''}` : null },
  ];

  return (
    <div className={`flex flex-col gap-3.5 px-5 py-4 border-b ${tone.band}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <Icon className={`h-[18px] w-[18px] shrink-0 mt-px ${tone.icon}`} aria-hidden="true" />
          <div>
            <p className={`text-sm font-semibold ${tone.text}`}>{title}</p>
            {summary && <p className="text-[13px] leading-[18px] text-zinc-600 dark:text-zinc-400">{summary}</p>}
          </div>
        </div>
        {tag && (
          <Tooltip>
            <TooltipTrigger className="cursor-default">
              <Badge variant="outline" size="tag" className={`bg-card ${tone.text} ${tone.edge}`}>{tag}</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>{CHURN_TYPE_CONFIG[churn.type]?.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <dl className="ml-7 grid grid-cols-3 gap-3">
        {facts.map((fact) => (
          <div key={fact.label} className="min-w-0">
            <dt className="text-xs text-zinc-600 dark:text-zinc-400">{fact.label}</dt>
            <dd className={`mt-1 text-sm font-medium truncate ${fact.value ? '' : 'text-zinc-600 dark:text-zinc-400'}`}>
              {fact.value ?? 'Not recorded'}
            </dd>
          </div>
        ))}
      </dl>
      <div className="ml-7">
        <p className="text-xs text-zinc-600 dark:text-zinc-400">Reason they gave</p>
        {churn.reason ? (
          <blockquote className={`mt-1.5 rounded-lg border bg-card px-3 py-2.5 text-sm font-medium text-foreground whitespace-pre-wrap break-words ${tone.edge}`}>
            {churn.reason}
          </blockquote>
        ) : (
          <p className={`mt-1.5 rounded-lg border border-dashed px-3 py-2.5 text-sm text-zinc-600 dark:text-zinc-400 ${tone.edge}`}>
            No reason on record
          </p>
        )}
      </div>
    </div>
  );
}

// Human-readable onboarding step labels (raw enum e.g. "book_call" reads poorly).
const ONBOARDING_STEP_LABELS: Record<string, string> = {
  wizard: 'Wizard',
  plans: 'Plans',
  checkout: 'Checkout',
  book_call: 'Book a call',
  completed: 'Completed',
};

export default function AgencyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listParams] = useSearchParams();

  const [agency, setAgency] = useState<AgencyDetailRecord | null>(null);
  const [subaccounts, setSubaccounts] = useState<AgencySubaccount[]>([]);
  const [team, setTeam] = useState<AgencyTeamMember[]>([]);
  
  // Subaccounts tab state
  const [subaccountSearch, setSubaccountSearch] = useState('');
  const [debouncedSubSearch, setDebouncedSubSearch] = useState('');
  const [subaccountDateRange, setSubaccountDateRange] = useState<DateRange | undefined>(undefined);
  const [uniqueMode, setUniqueMode] = useState(true);
  const [subPage, setSubPage] = useState(1);
  const [subMeta, setSubMeta] = useState<PaginationMeta | null>(null);
  const [isSubLoading, setIsSubLoading] = useState(false);

  // Team tab state
  const [teamSearch, setTeamSearch] = useState('');
  const [debouncedTeamSearch, setDebouncedTeamSearch] = useState('');
  const [teamRoleFilter, setTeamRoleFilter] = useState('all');
  const [teamStatusFilter, setTeamStatusFilter] = useState('all');
  const [teamPage, setTeamPage] = useState(1);
  const [teamMeta, setTeamMeta] = useState<PaginationMeta | null>(null);
  const [isTeamLoading, setIsTeamLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingAgency, setEditingAgency] = useState<AgencyDetailRecord | null>(null);
  const [isEditingUsage, setIsEditingUsage] = useState(false);
  const [isAttributingAffiliate, setIsAttributingAffiliate] = useState(false);
  const isViewer = useIsViewer();
  const { user } = useAuth();
  const canEditSubmissionUsage = useCanEditSubmissionUsage();
  const canShadowAccess = useCanShadowAccess();

  // Debounce subaccount search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSubSearch(subaccountSearch);
      setSubPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [subaccountSearch]);

  // Debounce team search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTeamSearch(teamSearch);
      setTeamPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [teamSearch]);

  // Reset subaccount page when date filter changes
  useEffect(() => {
    setSubPage(1);
  }, [subaccountDateRange]);

  // Reset team page when role filter changes
  useEffect(() => {
    setTeamPage(1);
  }, [teamRoleFilter]);

  const handleToggleShadow = async (checked: boolean) => {
    if (!id) return;
    setIsToggling(true);
    try {
      const data = await agenciesService.toggleShadowAccess(id, checked);
      setAgency(prev => prev ? { ...prev, hasShadowAccess: data.enabled } : null);
    } catch (err) {
      console.error('Failed to toggle shadow access', err);
    } finally {
      setIsToggling(false);
    }
  };

  // The list hands its own query string down when a row is opened, so going back
  // restores the same filters, search and page. Campaigns marks its query with
  // from=campaigns, so the arrow returns there instead of the Agencies list.
  const goBackToList = () => {
    navigate(listPathFor(listParams));
  };

  const handleDeleteAgency = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await agenciesService.deleteAgency(id);
      toastService.success('Agency deleted');
      goBackToList();
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't delete agency");
    } finally {
      setIsDeleting(false);
    }
  };

  // Load agency detail (once on mount)
  useEffect(() => {
    if (!id) return;
    const fetchAgency = async () => {
      setIsLoading(true);
      try {
        const agencyData = await agenciesService.getAgency(id);
        setAgency(agencyData);
      } catch (error) {
        console.error("Failed to load agency details", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgency();
  }, [id]);

  // Load subaccounts (reactive to page/search/date/unique)
  const loadSubaccounts = useCallback(async () => {
    if (!id) return;
    setIsSubLoading(true);
    try {
      const result = await agenciesService.getAgencySubaccounts(id, {
        page: subPage,
        search: debouncedSubSearch || undefined,
        dateFrom: subaccountDateRange?.from
          ? `${subaccountDateRange.from.getFullYear()}-${String(subaccountDateRange.from.getMonth() + 1).padStart(2, "0")}-${String(subaccountDateRange.from.getDate()).padStart(2, "0")}`
          : undefined,
        dateTo: subaccountDateRange?.to
          ? `${subaccountDateRange.to.getFullYear()}-${String(subaccountDateRange.to.getMonth() + 1).padStart(2, "0")}-${String(subaccountDateRange.to.getDate()).padStart(2, "0")}`
          : undefined,
        unique: uniqueMode,
      });
      setSubaccounts(result.data);
      setSubMeta(result.meta);
    } catch (error) {
      console.error("Failed to load subaccounts", error);
    } finally {
      setIsSubLoading(false);
    }
  }, [id, subPage, debouncedSubSearch, subaccountDateRange, uniqueMode]);

  useEffect(() => { loadSubaccounts(); }, [loadSubaccounts]);

  // Load team (reactive to page/search/role)
  const loadTeam = useCallback(async () => {
    if (!id) return;
    setIsTeamLoading(true);
    try {
      const result = await agenciesService.getAgencyTeam(id, {
        page: teamPage,
        search: debouncedTeamSearch || undefined,
        role: teamRoleFilter !== 'all' ? teamRoleFilter : undefined,
      });
      setTeam(result.data);
      setTeamMeta(result.meta);
    } catch (error) {
      console.error("Failed to load team members", error);
    } finally {
      setIsTeamLoading(false);
    }
  }, [id, teamPage, debouncedTeamSearch, teamRoleFilter]);

  useEffect(() => { loadTeam(); }, [loadTeam]);


  if (isLoading || !agency) {
    return <AgencyDetailSkeleton />;
  }

  const isTrialing = agency.stripeStatus === 'trialing';
  const trialEndsAt = agency.stripeTrialEndsAtUtc ? new Date(agency.stripeTrialEndsAtUtc) : null;
  const trialDaysLeft = trialEndsAt ? Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div className="space-y-6 w-full animate-in fade-in duration-300 pb-12">
      {/* Header section */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={goBackToList} className="h-9 w-9">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{agency.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Shadow Access</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Switch
                      checked={!!agency.hasShadowAccess}
                      disabled={isToggling || !canShadowAccess}
                      onCheckedChange={handleToggleShadow}
                    />
                  </span>
                </TooltipTrigger>
                {!canShadowAccess && <TooltipContent>{NO_CLIENT_ACCESS}</TooltipContent>}
              </Tooltip>
            </div>
            <AlertDialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="gap-2" disabled={isDeleting || isViewer}>
                        <Trash2 className="h-4 w-4" /> Delete Agency
                      </Button>
                    </AlertDialogTrigger>
                  </span>
                </TooltipTrigger>
                {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
              </Tooltip>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Agency</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete <strong>{agency.name}</strong> and all associated data including subaccounts, forms, submissions, and leads. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAgency}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Agency'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="subaccounts">
            Subaccounts
            <span className="ml-2 flex h-5 items-center justify-center rounded-full bg-primary/10 px-2 text-xs font-semibold text-primary">
              {subaccounts.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="team">
            Team Members
            <span className="ml-2 flex h-5 items-center justify-center rounded-full bg-primary/10 px-2 text-xs font-semibold text-primary">
              {team.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="xray">
            X-Ray
          </TabsTrigger>
          <TabsTrigger value="activity">
            Activity Log
          </TabsTrigger>
        </TabsList>

        {/* ────────────────────────────────────────────── */}
        {/* OVERVIEW TAB */}
        {/* ────────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-6">
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card className="py-0">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg shrink-0 bg-violet-100 dark:bg-violet-950/50 text-violet-600 dark:text-violet-400">
                    <FileText className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-2xl font-bold text-foreground leading-tight">
                      {agency.formsCount.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground truncate flex items-center gap-1.5">
                      Total Forms
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="py-0">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg shrink-0 bg-pink-100 dark:bg-pink-950/50 text-pink-600 dark:text-pink-400">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-2xl font-bold text-foreground leading-tight">
                      {(agency.completedSubmissionsCount ?? agency.submissionsCount).toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground truncate flex items-center gap-1.5">
                      Completed Submissions
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="py-0">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg shrink-0 bg-orange-100 dark:bg-orange-950/50 text-orange-600 dark:text-orange-400">
                    <Users className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-2xl font-bold text-foreground leading-tight">
                      {agency.leadsCount.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground truncate flex items-center gap-1.5">
                      Total Leads
                      {!!agency.partialLeadsCount && agency.partialLeadsCount > 0 && (
                        <Tooltip>
                          <TooltipTrigger className="cursor-help flex items-center">
                            <Info className="h-4 w-4 text-muted-foreground/70 hover:text-foreground transition-colors" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="flex flex-col gap-1 text-xs text-left">
                              <div><span className="font-medium">{(agency.completedLeadsCount ?? agency.leadsCount).toLocaleString()}</span> completed</div>
                              <div><span className="font-medium">{agency.partialLeadsCount.toLocaleString()}</span> partial</div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="py-0">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg shrink-0 bg-cyan-100 dark:bg-cyan-950/50 text-cyan-600 dark:text-cyan-400">
                    <CalendarCheck className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-2xl font-bold text-foreground leading-tight">
                      {agency.appointmentsCount.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground truncate flex items-center gap-1.5">
                      Appointments
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Agency Info */}
            <Card className="py-0 gap-0 shadow-sm">
              <CardHeader className="px-5 py-4 gap-0 border-b flex flex-row items-start sm:items-center justify-between">
                <div>
                  <CardTitle className="text-base">Agency Information</CardTitle>
                  <CardDescription>Core details and current status</CardDescription>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingUsage(true)}
                        disabled={!canEditSubmissionUsage || agency.submissionUsage?.limit == null}
                        className="h-8 gap-1.5 text-xs"
                      >
                        Edit
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!canEditSubmissionUsage ? (
                    <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>
                  ) : agency.submissionUsage?.limit == null ? (
                    <TooltipContent>This plan has no submission limit</TooltipContent>
                  ) : null}
                </Tooltip>
              </CardHeader>
              <CardContent className="p-5">
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Primary Admin</span>
                    <div className="max-w-[65%] flex-1 flex flex-col items-end min-w-0">
                      <TruncatedValue value={agency.superAdminName || 'Unknown'} wrapperClassName="w-full flex justify-end min-w-0" />
                      <TruncatedValue value={agency.superAdminEmail || ''} className="text-xs text-muted-foreground font-normal" wrapperClassName="w-full flex justify-end min-w-0" />
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Created At</span>
                    <span className="font-medium text-right">
                      <RelativeTime date={agency.createdAtUtc} />
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Sign up method</span>
                    <span className="font-medium text-right">
                      {agency.metadata?.signup?.method === 'email' ? 'Email' :
                       agency.metadata?.signup?.method ? <>Social (<span className="capitalize">{agency.metadata.signup.method}</span>)</> :
                       '—'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Referred via Affiliate</span>
                    {agency.affonsoToken && agency.affonsoTokenUnmatched ? (
                      // Affonso confirmed no affiliate has this signup code, so it credits no one and can be attributed again.
                      <div className="max-w-[65%] flex-1 flex items-center justify-end gap-2 min-w-0">
                        <TruncatedValue value={agency.affonsoToken} wrapperClassName="flex-1 flex justify-end min-w-0" />
                        <Button variant="outline" size="sm" onClick={() => setIsAttributingAffiliate(true)} className="h-7 text-xs shrink-0">
                          Reattribute
                        </Button>
                      </div>
                    ) : agency.affonsoToken ? (
                      <div className="max-w-[65%] flex-1 flex flex-col items-end min-w-0">
                        <TruncatedValue value={agency.referredByName || agency.affonsoToken} />
                        {agency.referredByEmail && (
                          <TruncatedValue value={agency.referredByEmail} className="text-xs text-muted-foreground font-normal" wrapperClassName="w-full flex justify-end min-w-0" />
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-right">No</span>
                        <Button variant="outline" size="sm" onClick={() => setIsAttributingAffiliate(true)} className="h-7 text-xs">
                          Attribute
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Submissions this month</span>
                    <span className="font-medium text-right">
                      {agency.submissionUsage?.limit != null
                        ? `${agency.submissionUsage.used.toLocaleString()} / ${agency.submissionUsage.limit.toLocaleString()}`
                        : 'Unlimited'}
                    </span>
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* Onboarding Details */}
            <Card className="py-0 gap-0 shadow-sm">
              <CardHeader className="px-5 py-4 gap-0 border-b">
                <CardTitle className="text-base">Onboarding Profile</CardTitle>
                <CardDescription>Answers and completion status</CardDescription>
              </CardHeader>
              <CardContent className="p-5">
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Current Step</span>
                    <span className="font-medium text-right">{ONBOARDING_STEP_LABELS[agency.onboardingStep] ?? agency.onboardingStep}</span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Completed On</span>
                    <div className="max-w-[65%] flex-1 flex justify-end min-w-0 font-medium">
                      {agency.onboardingCompletedAtUtc ? <RelativeTime date={agency.onboardingCompletedAtUtc} /> : '—'}
                    </div>
                  </div>
                  
                  {agency.onboardingAnswers && Object.keys(agency.onboardingAnswers).length > 0 && (
                    <div className="pt-4 border-t mt-4">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">Onboarding Answers</span>
                      <div className="space-y-4">
                        {Object.entries(agency.onboardingAnswers).map(([key, value]) => {
                          let displayValue = String(value);
                          // A phone answer also gets the country its number belongs to.
                          let country: string | undefined;
                          if (typeof value === 'object' && value !== null) {
                            if ('value' in value) displayValue = String((value as any).value);
                            else if ('countryCode' in value && 'number' in value) {
                              displayValue = formatPhoneDisplay(value as any);
                              country = phoneCountry(value as any);
                            }
                            else if ('number' in value) displayValue = String((value as any).number);
                            else displayValue = JSON.stringify(value);
                          }
                          return (
                            <Fragment key={key}>
                              <div className="flex justify-between items-center gap-4">
                                <span className="text-muted-foreground shrink-0 capitalize">{key.replace(/_/g, ' ')}</span>
                                <div className="max-w-[65%] flex-1 flex justify-end min-w-0">
                                  <TruncatedValue value={displayValue} wrapperClassName="w-full flex justify-end min-w-0" />
                                </div>
                              </div>
                              {country && (
                                <div className="flex justify-between items-center gap-4">
                                  <span className="text-muted-foreground shrink-0">Country (phone)</span>
                                  <span className="font-medium text-right">{countryName(country)}</span>
                                </div>
                              )}
                            </Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Right of the onboarding answers it follows on from. */}
            <SetupWizardCard agencyPublicId={id!} />

            {/* Billing & Subscription */}
            <Card className="py-0 gap-0 shadow-sm">
              <CardHeader className="px-5 py-4 gap-0 border-b flex flex-row items-start sm:items-center justify-between">
                <div>
                  <CardTitle className="text-base">Billing & Subscription</CardTitle>
                  <CardDescription>Financial health and status</CardDescription>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button variant="outline" size="sm" onClick={() => setEditingAgency(agency)} disabled={isViewer} className="h-8 gap-1.5 text-xs">
                        Edit
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
                </Tooltip>
              </CardHeader>
              {agency.churn && (
                <ChurnPanel churn={agency.churn} billingStatus={agency.billingStatus} timezone={user?.timezone} />
              )}
              <CardContent className="p-5">
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Plan</span>
                    <TruncatedValue value={agency.planName || 'Not selected'} />
                  </div>
                  {agency.extraSubaccounts > 0 && (
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Extra subaccounts</span>
                      <span className="font-medium text-right tabular-nums">
                        {agency.extraSubaccounts}
                        <span className="text-muted-foreground font-normal">
                          {' '}(${agency.extraSubaccountsMonthlyAmount.toLocaleString('en-US', {
                            minimumFractionDigits: Number.isInteger(agency.extraSubaccountsMonthlyAmount) ? 0 : 2,
                            maximumFractionDigits: 2,
                          })}/mo{isTrialing ? ' after the trial' : ''})
                        </span>
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Badge policy</span>
                    <span className="font-medium text-right">
                      {agency.brandingPolicy === 'always_on' ? 'Always on' : 'Legacy'}
                      {agency.brandingPolicy === 'always_on' && agency.brandingPolicyEffective === 'legacy' && (
                        <span className="text-muted-foreground"> (switch off, behaving as legacy)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Status</span>
                    {agency.planName || agency.manualAccess
                      ? getStatusBadge(agency.billingStatus)
                      : <span className="font-medium">—</span>}
                  </div>
                  {agency.manualAccess && (
                    <div className="flex justify-between items-start gap-4">
                      <span className="text-muted-foreground shrink-0">Manual access</span>
                      <span className="text-right">
                        <span className="block font-medium">{agency.manualAccessReason?.trim() || 'No reason given'}</span>
                        <span className="block text-xs text-muted-foreground">Removed automatically once they subscribe</span>
                      </span>
                    </div>
                  )}

                  {isTrialing && (
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Trial Days Left</span>
                      <span className="font-medium text-right">{Math.max(0, trialDaysLeft)}</span>
                    </div>
                  )}
                  {agency.trialReminderSentAtUtc && (
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Trial Reminder Sent</span>
                      <span className="font-medium text-right">
                        <RelativeTime date={agency.trialReminderSentAtUtc} />
                      </span>
                    </div>
                  )}
                  {/* The churn panel shows the end date and the reason itself. */}
                  {agency.stripeCurrentPeriodEndUtc && !agency.churn && (
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">{agency.cancelAtPeriodEnd ? 'Access Ends' : 'Next Billing Date'}</span>
                      <span className="font-medium text-right">
                        <RelativeTime date={agency.stripeCurrentPeriodEndUtc} />
                      </span>
                    </div>
                  )}
                  {agency.downgradeOfferClaimedAtUtc && (
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Downgrade Offer Claimed</span>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">Yes</Badge>
                    </div>
                  )}
                  {agency.cancelAtPeriodEnd && !agency.churn && (
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Cancels At Period End</span>
                      <Badge variant="destructive" className="font-medium">Yes</Badge>
                    </div>
                  )}
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Customer ID</span>
                    <div className="max-w-[65%] flex-1 flex justify-end min-w-0">
                      {agency.stripeCustomerId ? (
                        <TruncatedValue wrapperClassName="w-full flex justify-end min-w-0" className="font-mono text-primary hover:underline" value={agency.stripeCustomerId}>
                          <a href={`https://dashboard.stripe.com/customers/${agency.stripeCustomerId}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 justify-end truncate">
                            <span className="truncate">{agency.stripeCustomerId}</span> <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          </a>
                        </TruncatedValue>
                      ) : (
                        <span className="font-medium text-right font-mono">—</span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-muted-foreground shrink-0">Subscription ID</span>
                    <div className="max-w-[65%] flex-1 flex justify-end min-w-0">
                      {agency.stripeSubscriptionId ? (
                        <TruncatedValue wrapperClassName="w-full flex justify-end min-w-0" className="font-mono text-primary hover:underline" value={agency.stripeSubscriptionId}>
                          <a href={`https://dashboard.stripe.com/subscriptions/${agency.stripeSubscriptionId}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 justify-end truncate">
                            <span className="truncate">{agency.stripeSubscriptionId}</span> <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          </a>
                        </TruncatedValue>
                      ) : (
                        <span className="font-medium text-right font-mono">—</span>
                      )}
                    </div>
                  </div>
                  {agency.cancellationReason && !agency.churn && (
                    <div className="mt-4 pt-4 border-t">
                      <span className="text-muted-foreground block mb-2">Cancellation Reason</span>
                      <p className="text-sm font-medium bg-muted/50 p-3 rounded-md border text-foreground">
                        {agency.cancellationReason}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Signup Context */}
            {agency.metadata?.signup && (
              !!agency.metadata.signup.source ||
              !!agency.metadata.signup.referrer || 
              !!agency.metadata.signup.browser ||
              !!agency.metadata.signup.device ||
              !!agency.metadata.signup.country ||
              (agency.metadata.signup.params && Object.keys(agency.metadata.signup.params).length > 0)
            ) && (
              <Card className="py-0 gap-0 shadow-sm">
                <CardHeader className="px-5 py-4 gap-0 border-b">
                  <CardTitle className="text-base">Signup Context</CardTitle>
                  <CardDescription>Acquisition metadata captured at registration</CardDescription>
                </CardHeader>
                <CardContent className="p-5">
                  <div className="space-y-4 text-sm">
                    {agency.metadata.signup.source && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-muted-foreground shrink-0">Source</span>
                        <span className="font-medium text-right">
                          {agency.metadata.signup.params?.utm_source === 'form_badge' ? 'Form badge' :
                           agency.metadata.signup.source === 'signup' ? 'Signup Page (/signup)' :
                           agency.metadata.signup.source === 'register' ? 'Register Page (/register)' :
                           agency.metadata.signup.source === 'login' ? 'Login Page (/login)' :
                           <span className="capitalize">{agency.metadata.signup.source}</span>}
                        </span>
                      </div>
                    )}
                    {agency.signupSourceForm && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-muted-foreground shrink-0">Source form</span>
                        <span className="font-medium text-right truncate max-w-[65%]">
                          {agency.signupSourceForm.name} ·{' '}
                          <Link to={`/agencies/${agency.signupSourceForm.agencyPublicId}`} className="underline">
                            {agency.signupSourceForm.agencyName}
                          </Link>
                        </span>
                      </div>
                    )}
                    {agency.metadata.signup.country && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-muted-foreground shrink-0">Country (IP)</span>
                        <span className="font-medium text-right">{countryName(agency.metadata.signup.country)}</span>
                      </div>
                    )}
                    {agency.metadata.signup.referrer && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-muted-foreground shrink-0">Referrer</span>
                        <span className="font-medium text-right truncate max-w-[65%]">{agency.metadata.signup.referrer}</span>
                      </div>
                    )}
                    {agency.metadata.signup.browser && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-muted-foreground shrink-0">Browser</span>
                        <span className="font-medium text-right capitalize">{agency.metadata.signup.browser}</span>
                      </div>
                    )}
                    {agency.metadata.signup.device && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-muted-foreground shrink-0">Device</span>
                        <span className="font-medium text-right capitalize">{agency.metadata.signup.device}</span>
                      </div>
                    )}
                    {agency.metadata.signup.params && Object.keys(agency.metadata.signup.params).length > 0 && (
                      <div className="pt-4 border-t mt-4">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">URL Parameters</span>
                        <div className="space-y-2">
                          {Object.entries(agency.metadata.signup.params as Record<string, string>).map(([key, value]) => (
                            <div key={key} className="flex justify-between items-start gap-3">
                              <span className="text-xs text-muted-foreground font-mono shrink-0">{key}</span>
                              <span className="font-medium text-xs text-right break-all">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        </TabsContent>

        {/* ────────────────────────────────────────────── */}
        {/* SUBACCOUNTS TAB */}
        {/* ────────────────────────────────────────────── */}
        <TabsContent value="subaccounts" className="space-y-4">
          <div className="rounded-lg border bg-card">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search subaccounts..."
                  value={subaccountSearch}
                  onChange={(e) => setSubaccountSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center rounded-md border bg-muted p-0.5">
                  <button
                    type="button"
                    onClick={() => setUniqueMode(true)}
                    className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
                      uniqueMode
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Unique
                  </button>
                  <button
                    type="button"
                    onClick={() => setUniqueMode(false)}
                    className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
                      !uniqueMode
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    All
                  </button>
                </div>
                <DateRangePicker
                  dateRange={subaccountDateRange}
                  onDateRangeChange={setSubaccountDateRange}
                  className="w-full sm:w-auto text-sm"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-full min-w-[200px]">Account Name</TableHead>
                    <TableHead className="text-center w-[80px]">Members</TableHead>
                    <TableHead className="text-center w-[80px]">Forms</TableHead>
                    <TableHead className="text-center w-[120px]">Completed Submissions</TableHead>
                    <TableHead className="text-center w-[80px]">Leads</TableHead>
                    <TableHead className="text-center w-[100px]">Qualified</TableHead>
                    <TableHead className="text-center w-[80px]">Appts</TableHead>
                    <TableHead className="text-center w-[110px]">Qualified %</TableHead>
                    <TableHead className="text-center w-[100px]">Optin %</TableHead>
                    <TableHead className="text-center w-[110px]">Book Call %</TableHead>
                    <TableHead className="text-right w-[120px]">Created At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className={isSubLoading ? 'pointer-events-none' : ''}>
                    {isSubLoading ? (
                      <TableSkeleton columns={11} rows={5} />
                    ) : subaccounts.length === 0 && (debouncedSubSearch || subaccountDateRange) ? (
                      <TableRow>
                        <TableCell colSpan={11} className="h-48 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                              <Search className="h-6 w-6 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-medium mb-2">No results found</h3>
                            <p className="text-sm text-muted-foreground">
                              No subaccounts match your search or filters. Try adjusting your criteria.
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : subaccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="h-48 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                              <Building2 className="h-6 w-6 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-medium mb-2">No subaccounts yet</h3>
                            <p className="text-sm text-muted-foreground">
                              This agency doesn't have any subaccounts.
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      subaccounts.map(sub => (
                        <TableRow key={sub.id}>
                          <TableCell className="font-medium text-sm">
                            <span className="font-medium">{sub.name}</span>
                          </TableCell>
                          <TableCell className="text-center font-medium text-sm text-muted-foreground">{sub.teamMemberCount}</TableCell>
                          <TableCell className="text-center font-medium text-sm">{sub.formsCount}</TableCell>
                          <TableCell className="text-center font-medium text-sm">
                            {sub.completedSubmissionsCount ?? sub.submissionsCount}
                          </TableCell>
                          <TableCell className="text-center font-medium text-sm">
                            <div className="flex items-center justify-center gap-1.5">
                              {sub.leadsCount}
                              {!!sub.partialLeadsCount && sub.partialLeadsCount > 0 && (
                                <Tooltip>
                                  <TooltipTrigger className="cursor-help flex items-center">
                                    <Info className="h-3.5 w-3.5 text-muted-foreground/70 hover:text-foreground transition-colors" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="flex flex-col gap-1 text-xs text-left">
                                      <div><span className="font-medium">{sub.completedLeadsCount ?? sub.leadsCount}</span> completed</div>
                                      <div><span className="font-medium">{sub.partialLeadsCount}</span> partial</div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-medium text-sm">{sub.qualifiedCount}</TableCell>
                          <TableCell className="text-center font-medium text-sm">{sub.appointmentsCount}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-medium">{sub.qualifiedRate}%</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-medium">{sub.optinRate}%</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-medium">{sub.bookCallRate}%</Badge>
                          </TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground whitespace-nowrap">
                            <RelativeTime date={sub.createdAtUtc} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {subMeta && subMeta.totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-4 border-t">
                      <p className="text-sm text-muted-foreground">
                          Showing {subaccounts.length} of {subMeta.total} subaccounts
                      </p>
                      <Pagination className={`mx-0 w-auto ${isSubLoading ? 'pointer-events-none opacity-50' : ''}`}>
                          <PaginationContent>
                              <PaginationItem>
                                  <PaginationPrevious
                                      onClick={() => setSubPage(Math.max(1, subPage - 1))}
                                      className={subPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                              </PaginationItem>
                              {Array.from({ length: subMeta.totalPages }, (_, i) => i + 1).map((page) => (
                                  <PaginationItem key={page}>
                                      <PaginationLink
                                          onClick={() => setSubPage(page)}
                                          isActive={page === subPage}
                                          className="cursor-pointer"
                                      >
                                          {page}
                                      </PaginationLink>
                                  </PaginationItem>
                              ))}
                              <PaginationItem>
                                  <PaginationNext
                                      onClick={() => setSubPage(Math.min(subMeta.totalPages, subPage + 1))}
                                      className={subPage === subMeta.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                              </PaginationItem>
                          </PaginationContent>
                      </Pagination>
                  </div>
              )}
            </div>
        </TabsContent>

        {/* ────────────────────────────────────────────── */}
        {/* TEAM MEMBERS TAB */}
        {/* ────────────────────────────────────────────── */}
        <TabsContent value="team" className="space-y-4">
          <div className="rounded-lg border bg-card">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search members..."
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <Select value={teamRoleFilter} onValueChange={setTeamRoleFilter}>
                  <SelectTrigger className="w-full sm:w-[150px]">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Role:</span>
                      <SelectValue placeholder="All" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="agency_admin">Agency Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={teamStatusFilter} onValueChange={setTeamStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[150px]">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Status:</span>
                      <SelectValue placeholder="All" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="min-w-[200px]">Name</TableHead>
                    <TableHead className="w-[180px] text-center">Email</TableHead>
                    <TableHead className="w-[140px] text-center">Role</TableHead>
                    <TableHead className="w-[100px] text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className={isTeamLoading ? 'pointer-events-none' : ''}>
                  {isTeamLoading ? (
                    <TableSkeleton columns={4} rows={5} />
                  ) : team.length === 0 && (debouncedTeamSearch || teamRoleFilter !== 'all') ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-48 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                            <Search className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-medium mb-2">No results found</h3>
                          <p className="text-sm text-muted-foreground">
                            No team members match your search or filters. Try adjusting your criteria.
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : team.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-48 text-center">
                        <div className="flex flex-col items-center justify-center text-muted-foreground py-8">
                          <Users className="h-8 w-8 mb-4 text-muted-foreground/50" />
                          <p className="text-sm font-medium text-foreground">No team members found</p>
                          <p className="text-xs">This agency has no team members.</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    team.map(member => (
                      <TableRow key={`${member.id}-${member.level}-${member.subaccountName}`}>
                        <TableCell>
                          <span className="font-medium">{member.name && member.name.trim() ? member.name : 'Unknown'}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {member.email || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {member.role === 'super_admin' ? 'Super Admin' : 
                           member.role === 'admin' ? 'Admin' : 
                           member.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge tone={memberStatusTone(member.status || 'active')}>
                            {(member.status || 'active').charAt(0).toUpperCase() + (member.status || 'active').slice(1)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {teamMeta && teamMeta.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-4 border-t">
                    <p className="text-sm text-muted-foreground">
                        Showing {team.length} of {teamMeta.total} members
                    </p>
                    <Pagination className={`mx-0 w-auto ${isTeamLoading ? 'pointer-events-none opacity-50' : ''}`}>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => setTeamPage(Math.max(1, teamPage - 1))}
                                    className={teamPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                            </PaginationItem>
                            {Array.from({ length: teamMeta.totalPages }, (_, i) => i + 1).map((page) => (
                                <PaginationItem key={page}>
                                    <PaginationLink
                                        onClick={() => setTeamPage(page)}
                                        isActive={page === teamPage}
                                        className="cursor-pointer"
                                    >
                                        {page}
                                    </PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem>
                                <PaginationNext
                                    onClick={() => setTeamPage(Math.min(teamMeta.totalPages, teamPage + 1))}
                                    className={teamPage === teamMeta.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
          </div>
        </TabsContent>

        {/* ────────────────────────────────────────────── */}
        {/* ACTIVITY LOG TAB */}
        {/* ────────────────────────────────────────────── */}
        <TabsContent value="xray" className="space-y-4">
          <div className="mx-auto max-w-5xl">
            <AgencyXrayPanel agencyPublicId={id!} />
          </div>
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          <ActivityLogTimeline agencyId={id!} agencyCreatedAtUtc={agency.createdAtUtc} />
        </TabsContent>
      </Tabs>

      {editingAgency && (
        <EditAgencyModal
          open={!!editingAgency}
          onOpenChange={(open) => {
            if (!open) setEditingAgency(null);
          }}
          agency={editingAgency}
          onSuccess={() => {
            if (id) {
              agenciesService.getAgency(id).then(setAgency);
            }
          }}
        />
      )}

      {(!agency.affonsoToken || agency.affonsoTokenUnmatched) && (
        <AttributeAffiliateModal
          open={isAttributingAffiliate}
          onOpenChange={setIsAttributingAffiliate}
          agencyId={agency.id}
          agencyName={agency.name}
          unmatchedCode={agency.affonsoTokenUnmatched ? agency.affonsoToken : null}
          onSuccess={() => {
            if (id) {
              agenciesService.getAgency(id).then(setAgency);
            }
          }}
        />
      )}

      {agency.submissionUsage?.limit != null && (
        <EditSubmissionUsageModal
          open={isEditingUsage}
          onOpenChange={setIsEditingUsage}
          agencyId={agency.id}
          usage={agency.submissionUsage}
          onSuccess={() => {
            if (id) {
              agenciesService.getAgency(id).then(setAgency);
            }
          }}
        />
      )}
    </div>
  );
}
